import React from "react";
import {Alert} from "react-bootstrap";
import {PiWarningCircle} from "react-icons/pi";
import HelpTooltip from "../HelpTooltip.jsx";
import {formatDateDE} from "./format.js";

/**
 * Unmissverständlicher Hinweis, dass jede Zahl auf dieser Seite eine
 * SCHÄTZUNG ist - Freigabe 22.09.2026, wörtlich: "das muss in der
 * Oberfläche unmissverständlich dastehen, nicht im Kleingedruckten." Steht
 * deshalb bewusst als durchgehend sichtbarer Alert-Kasten direkt unter dem
 * Seitentitel, nicht in einem Tooltip oder einer einklappbaren Box.
 *
 * Zeigt außerdem die aktive Preisgrundlage (Brutto/Netto) direkt an der
 * Kennzahl, nicht nur im Tooltip (zweite Vorgabe derselben Freigabe), und
 * die Belastbarkeit des gewählten Zeitraums nach Datenlage (dritte
 * Vorgabe: "sage in der Oberfläche ehrlich, ab wann belastbar gerechnet
 * werden kann").
 */
export default function SchaetzungHinweis({preisgrundlage, belastbarkeit}) {
    const wertebasisText = preisgrundlage === "netto" ? "Nettoumsätzen" : "Bruttoumsätzen";

    return (
        <Alert variant="warning" className="d-flex align-items-start gap-2 mb-3">
            <PiWarningCircle size={22} className="flex-shrink-0 mt-1" aria-hidden="true" />
            <div className="small">
                <div className="fw-semibold mb-1">
                    Schätzung, keine Ist-Zahl — gerechnet mit {wertebasisText}
                    <HelpTooltip placement="right" className="ms-1">
                        Für jeden Farbton und jede Kalenderwoche wird die Differenz aus dem von Cosmos berechneten
                        „bereinigten Wochenbedarf" (was verkauft worden wäre, wäre der Farbton immer lieferbar
                        gewesen) und der tatsächlich fakturierten Menge gebildet. Diese Differenz, multipliziert mit
                        dem hinterlegten Verkaufspreis, ist der geschätzte entgangene Umsatz — kein gebuchter Betrag.
                        Negative Differenzen (mehr verkauft als der bereinigte Bedarf) fließen mit 0 statt mit einem
                        negativen Wert ein.
                    </HelpTooltip>
                </div>
                <div>
                    Ob mit Brutto- oder Nettoverkaufspreis gerechnet wird, lässt sich unten umstellen. Bei Brutto
                    wird der hinterlegte Nettopreis mit dem angenommenen Regelsteuersatz hochgerechnet.
                </div>
                {belastbarkeit && (
                    <div className="mt-2">
                        <strong>Belastbarkeit des gewählten Zeitraums:</strong>{" "}
                        {belastbarkeit.wochen_belastbar} von {belastbarkeit.wochen_gesamt} Wochen sind voll
                        belastbar gerechnet (ab {formatDateDE(belastbarkeit.bestandshistorie_ab)}, mit
                        Bestandshistorie).{" "}
                        {belastbarkeit.wochen_eingeschraenkt > 0 && (
                            <>
                                {belastbarkeit.wochen_eingeschraenkt} Wochen ({formatDateDE(belastbarkeit.unerfuellte_nachfrage_ab)}
                                {" "}bis {formatDateDE(belastbarkeit.bestandshistorie_ab)}) sind nur eingeschränkt belastbar — es
                                fließt lediglich die gemeldete unerfüllte Nachfrage ein, keine Bestandslücken-Korrektur.{" "}
                            </>
                        )}
                        {belastbarkeit.wochen_schwach > 0 && (
                            <>
                                {belastbarkeit.wochen_schwach} Wochen (vor {formatDateDE(belastbarkeit.unerfuellte_nachfrage_ab)}) sind
                                kaum belastbar und zeigen den entgangenen Umsatz eher zu niedrig als zu hoch.{" "}
                            </>
                        )}
                        Vor {formatDateDE(belastbarkeit.daten_ab)} liefert Cosmos wegen der Umstellung der
                        Buchungsweise (Übernummer → Einzelfarbton) ohnehin keine Zahlen — der Zeitraum beginnt
                        deshalb nicht früher.
                    </div>
                )}
            </div>
        </Alert>
    );
}
